import { useCallback, useRef } from "react";
import { useDebouncedCallback } from "use-debounce";

export type UseScrollEndOptions = {
    /** Callback function that gets triggered when the user scrolls to the end */
    onScrollEnd?: () => void;
    /** Threshold in pixels from the bottom to trigger onScrollEnd (default: 100) */
    threshold?: number;
    /** Debounce delay in milliseconds (default: 500) */
    debounceMs?: number;
};

/**
 * Hook that provides a scroll handler for detecting when user scrolls near the end of a container.
 * The callback is debounced to prevent excessive calls during scrolling.
 *
 * @example
 * ```tsx
 * const { scrollContainerRef, onScroll } = useScrollEnd({
 *     onScrollEnd: () => {
 *         fetchNextPage();
 *     },
 *     threshold: 100,
 * });
 *
 * return <div ref={scrollContainerRef} onScroll={onScroll}>...</div>
 * ```
 */
export function useScrollEnd({ onScrollEnd, threshold = 100, debounceMs = 500 }: UseScrollEndOptions) {
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const debouncedOnScrollEnd = useDebouncedCallback(
        () => {
            onScrollEnd?.();
        },
        debounceMs,
        {
            leading: true,
            trailing: true,
        },
    );

    const handleScroll = useCallback(() => {
        if (!scrollContainerRef.current || !onScrollEnd) return;

        const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
        const bottom = scrollHeight - clientHeight;
        const isAtBottom = scrollTop >= bottom - threshold;

        // Check if we're near the end of the scroll area and trigger the callback if provided
        if (isAtBottom) {
            debouncedOnScrollEnd();
        }
    }, [onScrollEnd, debouncedOnScrollEnd, threshold]);

    return {
        scrollContainerRef,
        onScroll: handleScroll,
    };
}
